// Hook: useFilters
// This custom hook centralizes filter state management for a filter drawer.
// It tracks applied filters and draft (unsaved) filters, provides helpers to update filters,
// detect unsaved changes, reset or clear filters, and apply changes back to the parent.

import { useState, useMemo, useEffect } from "react";

/**
 * Generate default filter values based on configuration.  Multi-select filters default
 * to an empty array; single-select filters default to null.  
 * If a default value is provided in the config, that value is used instead.
 *
 * @param {Array} config - Array of filter definitions
 * @returns {Object} defaultFilters - key/value pairs of defaults
 */
function getDefaultFilters(config) {
    // reduce : first arg is accumulator
    // second arg is a destructured current value from config array 
    // the resulting value will have a default value defined for each key (filter keyword)
    return config.reduce((acc, { key, type, defaultValue }) => {
        if (typeof defaultValue !== "undefined") {
            acc[key] = defaultValue;
        } else {
            acc[key] = type === "multi" ? [] : null;
        }
        return acc;
    }, {});
}

/**
 * Initialize filters based on defaults and any existing applied values.  If an
 * applied value is provided but undefined or null, fall back to the default.
 * @param {Object|undefined} initialApplied - filters from Redux/cookies
 * @param {Object} defaults - default filter values
 * @returns {Object} combined initial filters
 */
function initFilters(initialApplied, defaults) {
    const result = { ...defaults };
    if (initialApplied) {
        Object.keys(defaults).forEach((key) => {
            const val = initialApplied[key];
            if (typeof val !== "undefined" && val !== null) {
                // Clone arrays to avoid mutating external state
                result[key] = Array.isArray(defaults[key]) ? [...val] : val;
            }
        });
    }
    return result;
}

/**
 * Compare two filter objects for equality.  Multi-select filters (arrays) are
 * considered equal if they contain the same values in the same order.  Single-
 * select values are compared directly.
 * @param {Object} a - first filter state
 * @param {Object} b - second filter state
 * @returns {boolean} true if equal, false otherwise
 */
function isSameFilters(a, b) {
    const keys = Object.keys(a);
    for (const key of keys) {
        const valA = a[key];
        const valB = b[key];
        if (Array.isArray(valA) && Array.isArray(valB)) {
            if (valA.length !== valB.length) return false;
            for (let i = 0; i < valA.length; i++) {
                if (valA[i] !== valB[i]) return false;
            }
        } else if (valA !== valB) {
            return false;
        }
    }
    return true;
}

/**
 * Hook to manage filter state for a filter drawer.
 *
 * @param {Object} params
 * @param {Array} params.config - Array of filter definitions ({ key, type, label, options, defaultValue, enabled })
 * @param {Object} params.initialApplied - The initial applied filters (e.g., from Redux/cookies)
 * @param {Function} params.onApply - Callback to execute when filters are applied
 * @param {Function} params.onClear - Callback to execute when filters are cleared
 * @returns {Object} API exposing filter state and helper functions
 */
export default function useFilters({ config, initialApplied = {}, onApply, onClear }) {
    const defaults = useMemo(() => getDefaultFilters(config), [config]);
    // appliedFilters: currently active filters on the table
    const [appliedFilters, setAppliedFilters] = useState(() => initFilters(initialApplied, defaults));
    // draftFilters: unsaved selections in the drawer
    const [draftFilters, setDraftFilters] = useState(() => initFilters(initialApplied, defaults));
    // whether the drawer is open
    const [open, setOpen] = useState(false);

    // Update draft and applied filters if initialApplied changes (e.g., on first load)
    useEffect(() => {
        const nextFilters = initFilters(initialApplied, defaults);
        // Only update internal state when the actual filter values have changed.
        const shouldUpdateApplied = !isSameFilters(nextFilters, appliedFilters);
        const shouldUpdateDraft = !isSameFilters(nextFilters, draftFilters);
        if (shouldUpdateApplied || shouldUpdateDraft) {
            setAppliedFilters(nextFilters);
            setDraftFilters(nextFilters);
        }
    }, []);

    // Derived state: number of applied filters (at least one value)
    const appliedCount = useMemo(() => {
        return Object.keys(appliedFilters).reduce((count, key) => {
            const val = appliedFilters[key];
            if (Array.isArray(val)) {
                return count + (val.length > 0 ? 1 : 0);
            }
            return count + (val !== null && val !== undefined && val !== "" ? 1 : 0);
        }, 0);
    }, [appliedFilters]);

    // Derived state: whether draft differs from applied
    const hasUnsavedChanges = useMemo(() => !isSameFilters(draftFilters, appliedFilters), [draftFilters, appliedFilters]);

    // Derived: summary list for "Filtered by" information
    const summaryList = useMemo(() => {
        const list = [];
        for (const { key, label, type } of config) {
            const val = appliedFilters[key];
            if (Array.isArray(val) && val.length > 0) {
                // multi-select: use count
                list.push(`${label} (${val.length})`);
            } else if (!Array.isArray(val) && val !== null && val !== undefined && val !== "") {
                // single-select: show the value
                list.push(`${label} (${val})`);
            }
        }
        return list;
    }, [appliedFilters, config]);

    /**
     * Update a draft filter value.  Does not apply the change.
     * For arrays, expects the caller to provide an array of selected values.
     * @param {string} key - filter key
     * @param {any} value - new value for the filter
     */
    const setDraftFilter = (key, value) => {
        setDraftFilters((prev) => ({ ...prev, [key]: value }));
    };

    /**
     * Clear a single draft filter to its default value.
     * @param {string} key - filter key
     */
    const clearDraftFilter = (key) => {
        setDraftFilters((prev) => ({ ...prev, [key]: defaults[key] }));
    };

    /**
     * Reset all draft filters to their default values (unsaved).
     */
    const resetDraftFilters = () => {
        setDraftFilters({ ...defaults });
    };

    /**
     * Apply the draft filters: persist them, update applied filters, call onApply, and close the drawer.
     */
    const applyFilters = () => {
        // If there are no unsaved changes, do nothing
        if (!hasUnsavedChanges) return;
        setAppliedFilters(draftFilters);
        // Caller should persist filters (e.g., Redux/cookie) and reload data
        if (typeof onApply === "function") {
            onApply(draftFilters);
        }
        setOpen(false);
    };

    /**
     * Clear all filters: reset both applied and draft to defaults and call onClear.
     */
    const clearAppliedFilters = () => {
        setAppliedFilters({ ...defaults });
        setDraftFilters({ ...defaults });
        if (typeof onClear === "function") {
            onClear(defaults);
        }
        setOpen(false);
    };

    const clearUnsavedFilter = () => {
        const result = { ...defaults };
        if (appliedFilters) {
            Object.keys(defaults).forEach((key) => {
                const val = appliedFilters[key];
                if (typeof val !== "undefined" && val !== null) {
                    // Clone arrays to avoid mutating external state
                    result[key] = Array.isArray(defaults[key]) ? [...val] : val;
                }
            });
        }
        setDraftFilters(result);
    }

    // Convenience methods to control drawer state
    const openDrawer = () => setOpen(true);
    const closeDrawer = () => setOpen(false);

    return {
        // current draft and applied filters
        draftFilters,
        appliedFilters,
        appliedCount,
        hasUnsavedChanges,
        summaryList,
        // drawer state
        open,
        openDrawer,
        closeDrawer,
        // actions
        setDraftFilter,
        clearDraftFilter,
        resetDraftFilters,
        applyFilters,
        clearAppliedFilters,
        clearUnsavedFilter
    };
}